import { computed, inject, ref, type Ref } from 'vue'
import type { AIChatSessionMessage } from '@/types/generated'

export interface VerdictChunk {
  type: 'verdict'
  court_judgement_uuid: string
  court: string | null
  date: string | null
  file_number: string | null
  excerpt: string | null
}

interface UseVerdictChunksOptions {
  message: Ref<AIChatSessionMessage & { _isStreaming?: boolean }>
}

export function useVerdictChunks({ message }: UseVerdictChunksOptions) {
  const streamingChunks = inject<Ref<any[]>>('streamingChunks', ref([]))
  const streamingVerdictContext = inject<Ref<any[]>>('streamingVerdictContext', ref([]))

  const isStreaming = computed(() => message.value._isStreaming === true)

  const verdictChunks = computed<VerdictChunk[]>(() => {
    const messageContent = message.value.content as any
    const chunks = isStreaming.value
      ? streamingChunks.value || []
      : Array.isArray(messageContent?.chunks)
        ? (messageContent.chunks as any[])
        : []

    // Deduplicate by UUID (same verdict may be cited multiple times inline)
    const seen = new Set<string>()
    const unique = chunks.filter((c): c is VerdictChunk => {
      if (c?.type !== 'verdict') return false
      if (seen.has(c.court_judgement_uuid)) return false
      seen.add(c.court_judgement_uuid)
      return true
    })

    if (!isStreaming.value) return unique

    // During streaming, hydrate metadata from the pre-loaded verdict context (keyed by UUID)
    const ctxByUuid = new Map(
      (streamingVerdictContext.value as any[]).map((v) => [v.court_judgement_uuid, v])
    )
    return unique.map((v) => {
      const ctx = ctxByUuid.get(v.court_judgement_uuid)
      return ctx ? { ...v, ...ctx } : v
    })
  })

  return { verdictChunks }
}
